<script setup lang="ts">

import { AppText } from '@shared/ui/typography'
import { RowSlot } from '@shared/ui/other'
import { LinkBreadcrumb } from '@shared/ui/menus'
import { AppInput } from '@shared/ui/inputs'
import { MultiSelectSearchable, NativeSelect, TzSelect } from '@shared/ui/fields'

import { useCustomerFields } from '../model/customer-fields.hook'

defineOptions({
  name: 'CustomerGeneralInfo',
})

const {
  hasForm,
  canViewCustomers,
  refKids,
  refFirstName,
  refLastName,
  refEmail,
  refPhone,
  refTimezone,
  refContactChannels,
  refReminderChannels,
  timezones,
  screenRecordingOptions,
  aiSummarizeOptions,
  pricingModelOptions,
  paymentProviderOptions,
  contactChannelOptions,
  reminderChannelOptions,
  setText,
  setTimezone,
  setScreenRecording,
  setAiSummarize,
  setPricingModel,
  setPaymentProvider,
  setContactChannels,
  setReminderChannels,
} = useCustomerFields()
</script>

<template>
  <div class="customer-info-group">
    <AppText name="body1-medium">
      General info
    </AppText>
    <div class="customer-info-group__list">
      <RowSlot label="Kids">
        <div class="customer-info-group__chips">
          <LinkBreadcrumb
            v-for="kid in refKids"
            :key="kid.id"
            :text="kid.name"
            :to="kid.url"
          />
        </div>
      </RowSlot>
      <RowSlot label="First name">
        <AppInput
          name="first_name"
          placeholder="Type here..."
          :model-value="refFirstName"
          :state="refFirstName ? 'static' : 'error'"
          :disabled="!hasForm"
          @update:model-value="value => setText('first_name', value)"
        />
      </RowSlot>
      <RowSlot label="Last name">
        <AppInput
          name="last_name"
          placeholder="Type here..."
          :model-value="refLastName"
          :disabled="!hasForm"
          @update:model-value="value => setText('last_name', value)"
        />
      </RowSlot>
      <template v-if="canViewCustomers && hasForm">
        <RowSlot label="Email">
          <AppInput
            name="email"
            placeholder="Type here..."
            :model-value="refEmail"
            :state="refEmail ? 'static' : 'error'"
            @update:model-value="value => setText('email', value)"
          />
        </RowSlot>
        <RowSlot label="Phone">
          <AppInput
            name="phone"
            placeholder="Type here..."
            :model-value="refPhone"
            :state="refPhone ? 'static' : 'error'"
            @update:model-value="value => setText('phone', value)"
          />
        </RowSlot>
      </template>
      <RowSlot label="TimeZone">
        <TzSelect
          :key="refTimezone"
          :value="refTimezone"
          :timezones="timezones"
          :disabled="!hasForm"
          @option:selected="setTimezone"
        />
      </RowSlot>
      <template v-if="canViewCustomers && hasForm">
        <RowSlot label="Screen recording">
          <NativeSelect
            name="screen_recording"
            :options="screenRecordingOptions"
            @change="setScreenRecording"
          />
        </RowSlot>
        <RowSlot label="AI summarize">
          <NativeSelect
            name="ai_summarize"
            :options="aiSummarizeOptions"
            @change="setAiSummarize"
          />
        </RowSlot>
        <RowSlot label="Price type">
          <NativeSelect
            name="price_type"
            :options="pricingModelOptions"
            @change="setPricingModel"
          />
        </RowSlot>
        <RowSlot label="Contact channels">
          <MultiSelectSearchable
            :value="refContactChannels"
            :options="contactChannelOptions"
            reduce-key="value"
            :placeholder="refContactChannels.length ? '' : 'no channel'"
            is-all-available
            is-available-position
            @selected="setContactChannels"
          />
        </RowSlot>
        <RowSlot label="Reminder channels">
          <MultiSelectSearchable
            :value="refReminderChannels"
            :options="reminderChannelOptions"
            reduce-key="value"
            :placeholder="refReminderChannels.length ? '' : 'no channel'"
            is-all-available
            @selected="setReminderChannels"
          />
        </RowSlot>
        <RowSlot label="Payment provider">
          <NativeSelect
            name="payment_provider"
            placeholder="-- Not selected --"
            :options="paymentProviderOptions"
            @change="setPaymentProvider"
          />
        </RowSlot>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss" src="./CustomerInfoGroup.scss" />
